import { Trans, useLingui } from "@lingui/react/macro";
import { uniqWith } from "lodash";
import { List, Pencil, Plus, X } from "lucide-react";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";

import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { SupportedData } from "../../../utils/data-utils";
import { GenericValue } from "../common/generic-value";
import { GenericValueField } from "../common/generic-value-field";
import { Patient } from "../patient";

export interface Submittable {
  submit: () => Promise<void>;
}

export const BaseDataVisitField = observer(function BaseDataVisitField<
  T extends { uuid: string },
>(props: {
  patient: Patient;
  ref: (h: Submittable) => void;
  associatedData: T[];
  existingData: T[];
  variableInfo: SupportedData;
  valueSelector: (data: T) => unknown;
  existingDataTable: (props: {
    data: T[];
    onSelect: (selected: string[]) => void;
    preselected: string[];
  }) => React.ReactNode;
  onSubmit: (update: {
    newValue?: unknown;
    editedValue?: { uuid: string; value: unknown };
    addedUuids?: string[];
    removedUuids?: string[];
  }) => Promise<void>;
}) {
  const { t } = useLingui();
  const [submitState, setSubmitState] = useState<"pending" | "done">();
  // Set when the inline editor is editing an existing datapoint's value in
  // place (via "Edit"), as opposed to entering a brand-new value.
  const [editingUuid, setEditingUuid] = useState<string | undefined>();

  const [draft] = useState(() =>
    observable({
      newValue: undefined as unknown,
      associatedData: props.associatedData.map((d) => d.uuid),
    }),
  );

  useEffect(() => {
    const h: Submittable = {
      submit: async () => {
        if (submitState !== undefined) {
          return; // Already submitted/submitting
        }

        try {
          setSubmitState("pending");

          if (editingUuid != null) {
            // Editing the value of an existing associated datapoint in place.
            const original = props.associatedData.find(
              (d) => d.uuid === editingUuid,
            );
            const originalValue = original
              ? props.valueSelector(original)
              : undefined;
            if (draft.newValue != null && draft.newValue !== originalValue) {
              await props.onSubmit({
                editedValue: { uuid: editingUuid, value: draft.newValue },
              });
            }
          } else if (draft.newValue != null) {
            // `!= null`, not truthiness: a valid new value can be `0` (e.g. an
            // integer symptom score), which truthiness would drop; but `null`
            // (an optional enum Select cleared to its placeholder) must not be
            // submitted, since the API has no null value type.
            await props.onSubmit({ newValue: draft.newValue });
          } else {
            await props.onSubmit({
              addedUuids: draft.associatedData.filter(
                (uuid) => !props.associatedData.some((d) => d.uuid === uuid),
              ),
              removedUuids: props.associatedData
                .filter(
                  (d) => !draft.associatedData.some((uuid) => d.uuid === uuid),
                )
                .map((d) => d.uuid),
            });
          }

          setSubmitState("done");
        } catch (error) {
          setSubmitState(undefined);
          throw error;
        }
      },
    };
    props.ref(h);
  }, [props, draft, submitState, editingUuid]);

  const existingData = useMemo(
    () =>
      uniqWith(
        [...props.associatedData, ...props.existingData],
        (a, b) => a.uuid === b.uuid,
      ),
    [props.associatedData, props.existingData],
  );

  const [editing, setEditing] = useState(props.associatedData.length === 0);
  const [existingDataModalOpen, setExistingDataModalOpen] = useState(false);
  // TODO: unit?

  const selectedData = existingData.find(
    (d) => d.uuid === draft.associatedData[0],
  );

  return (
    <div className="flex items-center gap-3">
      {editing && (
        <>
          {/* Same fixed-width value column as display mode, so the Cancel
              button lines up with the first action button. */}
          <div className="w-36">
            <GenericValueField
              attr="newValue"
              model={draft}
              variableDef={props.variableInfo}
              disabled={submitState !== undefined}
              showUnit
            />
          </div>

          {props.associatedData.length > 0 && (
            <div className="flex items-center gap-1">
              <FieldAction
                label={t`Cancel`}
                onClick={() => {
                  setEditing(false);
                  setEditingUuid(undefined);
                  delete draft.newValue;
                }}
                disabled={submitState !== undefined}
              >
                <X size={16} className="text-foreground" />
              </FieldAction>
            </div>
          )}
        </>
      )}

      {!editing && (
        <>
          {/* Fixed-width value column keeps the action toolbar aligned across
              rows regardless of each value's width. */}
          <div className="w-36 truncate">
            {draft.associatedData.length === 1 ? (
              <GenericValue
                value={
                  selectedData ? props.valueSelector(selectedData) : undefined
                }
                variableDef={props.variableInfo}
                showUnit
              />
            ) : (
              t`${draft.associatedData.length} selected`
            )}
          </div>

          <div className="flex items-center gap-1">
            {draft.associatedData.length === 1 && selectedData != null && (
              <FieldAction
                label={t`Edit value`}
                onClick={() => {
                  draft.newValue = props.valueSelector(selectedData);
                  setEditingUuid(selectedData.uuid);
                  setEditing(true);
                }}
                disabled={submitState !== undefined}
              >
                <Pencil size={16} className="text-foreground" />
              </FieldAction>
            )}

            {existingData.length > 0 && (
              <FieldAction
                label={t`Select from existing`}
                onClick={() => {
                  setExistingDataModalOpen(true);
                }}
                disabled={submitState !== undefined}
              >
                <List size={16} className="text-foreground" />
              </FieldAction>
            )}

            <FieldAction
              label={t`Enter new value`}
              onClick={() => {
                setEditing(true);
              }}
              disabled={submitState !== undefined}
            >
              <Plus size={16} className="text-foreground" />
            </FieldAction>
          </div>
        </>
      )}

      <Dialog
        open={existingDataModalOpen}
        onOpenChange={(open) => {
          if (!open && existingDataModalOpen) {
            // Cancel detected
            setExistingDataModalOpen(false);
            draft.associatedData = [...props.associatedData.map((d) => d.uuid)];
          }
        }}
      >
        {props.existingDataTable({
          data: existingData,
          onSelect: (selected) => {
            draft.associatedData = selected;
          },
          preselected: draft.associatedData,
        })}

        <div className="flex justify-center">
          <Button
            onClick={() => {
              setExistingDataModalOpen(false);
            }}
          >
            <Trans>Confirm</Trans>
          </Button>
        </div>
      </Dialog>
    </div>
  );
});

/** Compact icon action used in the visit-field toolbar. */
function FieldAction(props: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip content={props.label}>
      <Button
        variant="subtle"
        size="sm"
        aria-label={props.label}
        onClick={props.onClick}
        disabled={props.disabled}
      >
        {props.children}
      </Button>
    </Tooltip>
  );
}
